import React, { useEffect, useRef, useState } from 'react';

import { Button, Center, Loader, Stack, Text, Title } from '@mantine/core';
import { AxiosError } from 'axios';
import { useAuth } from 'context/auth-context';
import useCheckoutRecovery from 'hooks/auth/useCheckoutRecovery';
import useSalesPortalConfiguration from 'hooks/salesPortal/useSalesPortalConfiguration';
import Header from 'layouts/SalesPortalLayout/components/Header';
import { useNavigate, useParams } from 'react-router-dom';
import { ErrorMessage } from 'types';

import { CheckoutStep } from './SalesPortalBillingPlan/components/Checkout/checkoutFlowStates';
import {
  buildCheckoutStorageKey,
  persistRecoveredCheckout,
} from './SalesPortalBillingPlan/components/Checkout/checkoutSessionStorage';

// Copy per rejection reason. Every failure lands on the same friendly restart screen —
// the buyer can start the purchase again from the gym's catalog or contact the gym.
const RESTART_COPY: Record<string, { title: string; description: string }> = {
  CHECKOUT_RECOVERY_TOKEN_EXPIRED: {
    title: 'This link has expired',
    description:
      'Resume links work for 7 days. You can start your signup again from the catalog — it only takes a few minutes — or contact the gym for help.',
  },
  CHECKOUT_RECOVERY_TOKEN_REDEEMED: {
    title: 'This link has already been used',
    description:
      'Each resume link can only be used once. Start your signup again from the catalog, or contact the gym if you think something went wrong.',
  },
  DEFAULT: {
    title: "We couldn't resume your signup",
    description:
      'This link is no longer valid — it may have been replaced by a newer email, or the purchase may already be complete. Start again from the catalog or contact the gym.',
  },
};

export function restartCopyFor(errorCode: string | undefined): { title: string; description: string } {
  return RESTART_COPY[errorCode ?? ''] ?? RESTART_COPY.DEFAULT;
}

// Landing page for the abandoned-checkout recovery email (spec Item 11). The link is a
// GET the buyer clicks; the redemption itself is an explicit POST fired once on mount,
// so mail scanners that prefetch the URL never consume the single-use token. On
// success the server-held progress is written into session storage and the buyer is
// routed into the checkout, which rehydrates it exactly like a same-session return.
const SalesPortalResumePage = (): JSX.Element => {
  const { salesPortalSlug, token } = useParams<{ salesPortalSlug: string; token: string }>();
  const navigate = useNavigate();
  const { refreshMe } = useAuth();
  const { data: salesPortalConfig } = useSalesPortalConfiguration(salesPortalSlug);
  const { mutate: redeem } = useCheckoutRecovery();

  const attemptedRef = useRef<boolean>(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  useEffect(() => {
    if (attemptedRef.current) return;
    attemptedRef.current = true;

    if (!token) {
      setErrorCode('CHECKOUT_RECOVERY_TOKEN_INVALID');
      return;
    }

    redeem(
      { token },
      {
        onSuccess: response => {
          const { gym, packageId, billingPlanId, currentStep, progress } = response.data;
          const storageKey = buildCheckoutStorageKey(gym.gymId, packageId, billingPlanId);
          persistRecoveredCheckout(storageKey, progress, currentStep as CheckoutStep);
          // The POST signed the buyer in — pull the new session before the checkout mounts.
          refreshMe();
          navigate(`/sales-portal/${gym.salesPortalSlug}/${packageId}/${billingPlanId}`, { replace: true });
        },
        onError: (error: AxiosError<ErrorMessage>) => {
          setErrorCode(error.response?.data?.errorCode ?? 'CHECKOUT_RECOVERY_TOKEN_INVALID');
        },
      },
    );
  }, [token, redeem, refreshMe, navigate]);

  const header = <Header logo={salesPortalConfig?.data.logo} gymTitle={salesPortalConfig?.data.gym.title} />;

  if (errorCode) {
    const copy = restartCopyFor(errorCode);
    return (
      <>
        {header}
        <Stack className='content'>
          <Stack spacing='lg' align='center' p='xl' data-cy='checkoutRecoveryRestartScreen'>
            <Title order={2} align='center' fw={700}>
              {copy.title}
            </Title>
            <Text c='dimmed' size='sm' align='center' maw={500}>
              {copy.description}
            </Text>
            <Button
              size='lg'
              mt='md'
              onClick={() => navigate(`/sales-portal/${salesPortalSlug}`)}
              data-cy='checkoutRecoveryStartOver'
            >
              Start over
            </Button>
          </Stack>
        </Stack>
      </>
    );
  }

  return (
    <>
      {header}
      <Stack className='content'>
        <Center p='xl' data-cy='checkoutRecoverySigningIn'>
          <Stack align='center' spacing='sm'>
            <Loader />
            <Text c='dimmed' size='sm'>
              Signing you in and picking up where you left off…
            </Text>
          </Stack>
        </Center>
      </Stack>
    </>
  );
};

export default SalesPortalResumePage;
